import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate }   from 'react-router-dom';
import { onAuthStateChanged }  from 'firebase/auth';
import { deleteDoc, doc, collection, query, orderBy, onSnapshot, updateDoc, getDoc, getDocs, Timestamp, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db }            from '../../services/firebase';
import './AdminSlatesPage.css';
import { TickerPanel }          from '../../components/admin/TickerPanel';
import { AdminPlayerStatsBar } from '../../components/admin/AdminPlayerStatsBar';

// ─── Timestamp Compatibility Helper ──────────────────────────────────────────
//
// Timing fields may be Firestore Timestamps (new saves) or bare ISO strings
// (legacy docs pre-migration). toMs handles both safely.

function toMs(val) {
  if (!val) return null;
  if (typeof val?.toMillis === 'function') return val.toMillis();
  if (typeof val === 'string') { const ms = new Date(val).getTime(); return isNaN(ms) ? null : ms; }
  return null;
}

function toDate(val) {
  if (!val) return null;
  if (typeof val?.toDate === 'function') return val.toDate();
  if (typeof val === 'string') { const d = new Date(val); return isNaN(d.getTime()) ? null : d; }
  return null;
}

// ─── Derived Status ───────────────────────────────────────────────────────────
//
// Status is computed client-side from timestamps — no Firestore writes needed
// for routine open/close transitions.
//
// Priority order:
//   1. COMPLETED  — all races settled
//   2. CLOSED     — now >= gameCloseTime
//   3. LIVE       — admin forced open OR now >= gameOpenTime AND odds complete
//   4. WARNING    — now >= gameOpenTime BUT odds not entered
//   5. WAITING    — now < gameOpenTime (or no open time set)
//   6. BUILDING   — no races added yet / setup incomplete

function computeDerivedStatus(slate) {
  const now = Date.now();

  // Admin-forced open overrides everything below COMPLETED/CLOSED
  const forcedOpen = slate.status === 'live' || slate.status === 'entries_open';

  // Finalised — admin manually marked OR all races settled via results flow
  const allRacesSettled = (slate.completedRaces ?? 0) >= (slate.totalRaces ?? 1) && (slate.totalRaces ?? 0) > 0;
  if (slate.finalised === true || allRacesSettled) {
    return 'completed';
  }

  // Closed — gameCloseTime has passed
  const closeMs = toMs(slate.gameCloseTime);
  if (closeMs && now >= closeMs) return 'closed';

  // Odds complete flag — written by AdminOddsPage when all runners have odds
  const oddsComplete = slate.oddsComplete === true;

  // Open time
  const openMs = toMs(slate.gameOpenTime);
  const timeToOpen = openMs ? now >= openMs : false;

  if (forcedOpen) {
    // Admin pressed "Open to Public" — always live regardless of odds
    return 'live';
  }

  if (timeToOpen && !oddsComplete)                      return 'warning';  // Open time arrived but odds missing
  if (timeToOpen && oddsComplete && slate.pricesLocked)  return 'live';    // Open, odds ready, prices locked ✓
  if (timeToOpen && oddsComplete && !slate.pricesLocked) return 'opening'; // Open, odds ready, awaiting auto-lock

  // Building vs waiting — has races been added?
  if ((slate.totalRaces ?? 0) === 0) return 'building';

  return 'waiting';
}

// ─── Status Badge Config ──────────────────────────────────────────────────────

const STATUS_CONFIG = {
  building:  { label: 'Building',             cssClass: 'ss-status-badge--waiting'           },
  waiting:   { label: '● Opening Soon',       cssClass: 'ss-status-badge--opening-soon'      },
  opening:   { label: '⏳ Opening Soon',      cssClass: 'ss-status-badge--opening-soon'      },
  warning:   { label: '⚠ Odds Missing',       cssClass: 'ss-status-badge--warning'           },
  live:      { label: '● Live',               cssClass: 'ss-status-badge--live'              },
  closed:    { label: 'Closed',               cssClass: 'ss-status-badge--closed'            },
  completed: { label: '✓ Finalised',          cssClass: 'ss-status-badge--finalised'         },
  expired:   { label: '⚠ Results Pending',   cssClass: 'ss-status-badge--results-pending'   },
};

// ─── Slate Row ────────────────────────────────────────────────────────────────

const SlateRow = ({ slate, onOpenEntries, onForceOpen, onDelete, onArchive, onRateRunners, isRating, onRefreshFormAndRatings, isRefreshing }) => {
  const navigate = useNavigate();

  // Re-derive status every 10s so the badge flips automatically at open/close time
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(id);
  }, []);

  const derivedStatus = useMemo(() => {
    // Check date expiry first (meeting date in the past, not completed)
    const today = new Date().toISOString().split('T')[0];
    const isPast = slate.meetingDate && slate.meetingDate < today;
    if (isPast && slate.status !== 'completed' && !slate.finalised) return 'expired';
    return computeDerivedStatus(slate);
  }, [slate, now]); // eslint-disable-line react-hooks/exhaustive-deps

  const config = STATUS_CONFIG[derivedStatus] || STATUS_CONFIG.waiting;
  const isFree = !slate.entryFeeEnabled || slate.entryFeeAmount === 0;

  // "Open to Public" — only for warning/building (requires odds check)
  // "Force Open Now" — only for waiting/opening-soon (bypasses odds, overrides schedule)
  const canOpenManually = derivedStatus === 'warning' || derivedStatus === 'building';
  const canForceOpen    = derivedStatus === 'waiting';
  const oddsComplete    = slate.oddsComplete === true;

  return (
    <div className={`ss-adm-slate-row ${derivedStatus === 'warning' ? 'ss-adm-slate-row--warning' : ''}`}>

      {/* Status badge */}
      <div className="ss-adm-status-col">
        <span className={`ss-status-badge ${config.cssClass}`}>
          {config.label}
        </span>
      </div>

      {/* Game info */}
      <div className="ss-adm-info-col">
        <span className="ss-adm-slate-title">{slate.gameName || slate.title || 'Untitled Game Slate'}</span>
        <div className="ss-adm-slate-meta">
          <span>{slate.meetingDate}</span>
          <span>·</span>
          <span>{slate.totalRaces || 0} legs</span>
          <span>·</span>
          <span className={isFree ? 'ss-adm-fee--free' : 'ss-adm-fee--paid'}>
            {isFree ? 'Free entry' : `$${slate.entryFeeAmount?.toFixed(2)} entry`}
          </span>
        </div>

        {(slate.totalRaces ?? 0) > 0 && (() => {
          const total     = slate.totalRaces ?? 0;
          const completed = slate.completedRaces ?? 0;
          const pct       = total > 0 ? Math.round((completed / total) * 100) : 0;
          const isDone    = slate.finalised === true || derivedStatus === 'completed';

          return (
            <div className="ss-adm-results-progress">
              <div className="ss-adm-results-progress__label">
                {isDone
                  ? `All ${total} races resulted`
                  : `Resulted ${completed} of ${total} races`
                }
              </div>
              <div className="ss-adm-results-progress__track">
                <div
                  className={`ss-adm-results-progress__fill ${isDone ? 'ss-adm-results-progress__fill--done' : ''}`}
                  style={{ width: `${isDone ? 100 : pct}%` }}
                />
              </div>
            </div>
          );
        })()}

        {/* Warning banner — odds missing but open time has passed */}
        {derivedStatus === 'warning' && (
          <div className="ss-adm-odds-warning">
            ⚠ Game should be live now but odds have not been entered. Go to Runners &amp; Odds to complete.
          </div>
        )}

        {/* Critical warning — game is live but prices not locked, players cannot enter */}
        {derivedStatus === 'live' && !slate.pricesLocked && (
          <div className="ss-adm-prices-warning">
            🔴 PRICES NOT LOCKED — Players cannot enter
          </div>
        )}

        {/* Value-engine health guard — prices are locked + ratings OK, but
            value labels couldn't be computed (missing share prices). See
            valueRecalculation.js computeValueHealthWarning(). */}
        {slate.pricesLocked && slate.valueHealthWarning && (
          <div className="ss-adm-prices-warning" title={slate.valueHealthWarning}>
            ⚠ {slate.valueHealthWarning}
          </div>
        )}

        {/* Timing info — show open/close times when relevant */}
        {(derivedStatus === 'waiting' || derivedStatus === 'building') && slate.gameOpenTime && (
            <div className="ss-adm-timing-hint">
              Opens: {toDate(slate.gameOpenTime)?.toLocaleString('en-AU', { weekday: 'short', hour: '2-digit', minute: '2-digit' }) ?? '—'}
              {slate.gameCloseTime && (
                <> · Closes: {toDate(slate.gameCloseTime)?.toLocaleString('en-AU', { weekday: 'short', hour: '2-digit', minute: '2-digit' }) ?? '—'}</>
              )}
          </div>
        )}
      </div>

      {/* Player count */}
      <div className="ss-adm-stats-col">
        <div className="ss-adm-stat">
          <span className="ss-adm-stat-label">Players</span>
          <span className="ss-adm-stat-val">{slate.activePlayerCount || 0}</span>
        </div>
      </div>

      {/* Actions — two-row layout so buttons never overflow the card */}
      <div className="ss-adm-actions-col">

        {/* Row 1: primary actions (edit / open / rate) */}
        <div className="ss-adm-actions-row ss-adm-actions-row--primary">
          {/* Edit — building, opening soon, opening (awaiting lock), warning, and live */}
          {(derivedStatus === 'building' || derivedStatus === 'waiting' ||
            derivedStatus === 'opening'  || derivedStatus === 'warning' ||
            derivedStatus === 'live') && (
            <button
              className="ss-adm-action-btn ss-adm-action-btn--secondary"
              onClick={() => navigate(`/admin/setup/${slate.id}`)}
              type="button"
            >
              Edit
            </button>
          )}

          {/* Force Open Now — "Opening Soon" state only */}
          {canForceOpen && (
            <button
              className="ss-adm-action-btn ss-adm-action-btn--force-open"
              onClick={() => onForceOpen(slate.id, slate.gameName || slate.id)}
              title="Override the scheduled open time and open to players immediately"
              type="button"
            >
              Force Open Now
            </button>
          )}

          {/* Open to Public — warning/building states only */}
          {canOpenManually && (
            <button
              className="ss-adm-action-btn ss-adm-action-btn--open"
              onClick={() => !oddsComplete
                ? alert('Odds have not been entered for all runners. Please complete the Runners & Odds page before opening.')
                : onOpenEntries(slate.id)
              }
              title={!oddsComplete ? 'Odds must be entered before opening' : 'Open game to public now'}
              type="button"
            >
              Open to Public
            </button>
          )}

          {/* Rate Runners — available for non-finalised games; three visual states */}
          {derivedStatus !== 'completed' && (() => {
            const isStale = slate.ratingsStatus && slate.ratingsStatus !== 'OK';
            const hasRatings = slate.ratingsCalculated === true && !isStale;
            const ratedAt = slate.ratingsCalculatedAt
              ? new Date(
                  slate.ratingsCalculatedAt?.toDate?.()
                    || slate.ratingsCalculatedAt
                ).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })
              : null;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <button
                  className={`ss-adm-action-btn ss-btn-rate-runners ${
                    isRating
                      ? 'ss-btn-rate-runners--loading'
                      : hasRatings
                        ? 'ss-btn-rate-runners--done'
                        : 'ss-btn-rate-runners--pending'
                  }`}
                  onClick={() => onRateRunners(slate.id)}
                  disabled={isRating}
                  title={
                    isStale
                      ? `Ratings flagged ${slate.ratingsStatus} (thin/stale data) — click to recalculate, or use Refresh Form + Ratings`
                      : hasRatings
                        ? `Rated${ratedAt ? ' at ' + ratedAt : ''} — click to recalculate if odds changed`
                        : 'Compute runner ratings from odds, barrier, and other available data'
                  }
                  style={hasRatings ? { backgroundColor: '#16a34a', color: '#fff' } : isStale ? { backgroundColor: '#f59e0b', color: '#fff' } : undefined}
                  type="button"
                >
                  {isRating
                    ? '⏳ Rating…'
                    : isStale
                      ? '⚠ Ratings Stale/Thin'
                      : hasRatings
                        ? '⭐ Ratings Done ✓'
                        : '☆ Rate Runners'
                  }
                </button>
                <div style={{
                  fontSize: '10px', color: '#6B7280',
                  marginTop: '2px', textAlign: 'center',
                }}>
                  {hasRatings && ratedAt ? `Rated ${ratedAt}` : ''}
                </div>
                <button
                  className="ss-adm-action-btn"
                  onClick={() => onRefreshFormAndRatings(slate.id)}
                  disabled={isRefreshing}
                  title="Re-pull scraped form data from the meeting and recalculate ratings — use this if a race's ratings look stale or thin (e.g. missing career/form data)"
                  style={{ marginTop: '4px', fontSize: '11px', padding: '4px 8px' }}
                  type="button"
                >
                  {isRefreshing ? '⏳ Refreshing…' : '♻ Refresh Form + Ratings'}
                </button>
              </div>
            );
          })()}
        </div>

        {/* Row 2: outcome actions (results / archive / delete) */}
        <div className="ss-adm-actions-row ss-adm-actions-row--secondary">
          <button
            className="ss-adm-action-btn ss-adm-action-btn--secondary"
            onClick={() => navigate(`/admin/results?slate=${slate.id}`)}
            type="button"
          >
            Results
          </button>

          {/* Archive — soft-hides the game, preserves all player data */}
          {slate.archived ? (
            <span className="ss-adm-archived-badge">Archived</span>
          ) : (
            <button
              className="ss-adm-action-btn ss-adm-action-btn--archive"
              onClick={() => onArchive(slate.id, slate.gameName || slate.id)}
              type="button"
              title="Archive — hides from admin grid but preserves all player entries and results"
            >
              Archive
            </button>
          )}

          {/* Delete (permanent) — destructive, for test games only */}
          <button
            className="ss-adm-action-btn ss-adm-action-btn--danger"
            onClick={() => onDelete(slate.id, slate.gameName || slate.id)}
            type="button"
            title="Permanently delete — only use for test games with zero real entries"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Import status types ──────────────────────────────────────────────────────
const IMPORT_IDLE    = 'idle';
const IMPORT_LOADING = 'loading';
const IMPORT_SUCCESS = 'success';
const IMPORT_ERROR   = 'error';
const IMPORT_TIMEOUT = 'timeout'; // server-side likely ran but client timed out

// ─── Main Page ────────────────────────────────────────────────────────────────

export const AdminSlatesPage = () => {
  const [currentUser,  setCurrentUser]  = useState(null);
  const [slates,       setSlates]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [actionMsg,    setActionMsg]    = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const [importStatus,  setImportStatus]  = useState(IMPORT_IDLE);
  const [importResult,  setImportResult]  = useState(null);
  const [importCode,    setImportCode]    = useState('TB');
  const [importRange,   setImportRange]   = useState({ daysAhead: 1, startOffset: 0, label: 'today' });
  const [cleanupStatus, setCleanupStatus] = useState('idle'); // 'idle' | 'loading' | 'done' | 'error'
  const [cleanupResult, setCleanupResult] = useState(null);

  const [loungeRetentionDays,  setLoungeRetentionDays]  = useState(14);
  const [loungeCleanupLoading, setLoungeCleanupLoading] = useState(null); // null | 'dryrun' | 'delete'
  const [loungeCleanupResult,  setLoungeCleanupResult]  = useState(null);

  useEffect(() => { return onAuthStateChanged(auth, setCurrentUser); }, []);

  useEffect(() => {
    return onSnapshot(
      query(collection(db, 'game_slates'), orderBy('meetingDate', 'desc')),
      snap => {
        setSlates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }
    );
  }, []);

  // Force Open Now — immediately opens the game.
  //   Odds ready  → one click, no confirm, prices locked automatically.
  //   Odds NOT ready → confirm dialog warning before proceeding.
  const handleForceOpen = async (slateId, gameName) => {
    const gameRef  = doc(db, 'game_slates', slateId);
    const gameSnap = await getDoc(gameRef);
    const gameData = gameSnap.data();

    // Hard block (requirement: never let stale/thin ratings go live) — this
    // is the actual code path admins use to open games, so the guard has to
    // live here, not just in the Cloud Functions the admin UI bypasses.
    if (gameData?.ratingsStatus === 'STALE_OR_THIN_DATA') {
      setActionMsg(
        `Blocked: "${gameName}" ratings are flagged STALE_OR_THIN_DATA — more than half the field ` +
        `has little/no real form data. Run "♻ Refresh Form + Ratings" (or "☆ Rate Runners" once the ` +
        `underlying meeting data is enriched) before going live.`
      );
      return;
    }

    const oddsReady = gameData?.oddsComplete === true || gameData?.pricesLocked === true;

    if (!oddsReady) {
      const confirmed = window.confirm(
        `⚠ Odds have not been fully entered yet.\n\n` +
        `Force Open Now will make the game visible to players, but they will see "Fields Not Yet Set" ` +
        `until you lock prices manually on the Odds Entry page.\n\n` +
        `Continue anyway?`
      );
      if (!confirmed) return;
    }

    try {
      const now     = Timestamp.now();
      const payload = {
        status:        'live',
        gameOpenTime:  now,
        forceOpenedAt: now,
      };

      if (oddsReady) {
        payload.pricesLocked   = true;
        payload.pricesLockedAt = now;
      }

      await updateDoc(gameRef, payload);

      const msg = oddsReady
        ? `"${gameName}" is now LIVE — prices locked, players can enter.`
        : `"${gameName}" force-opened. Enter odds and lock prices to allow player entry.`;
      setActionMsg(msg);
      setTimeout(() => setActionMsg(null), 5000);
    } catch (err) {
      setActionMsg(`Error: ${err.message}`);
    }
  };

  // Manual open override — writes status flags to Firestore
  const handleOpenEntries = async (slateId) => {
    const gameRef  = doc(db, 'game_slates', slateId);
    const gameSnap = await getDoc(gameRef);
    const gameData = gameSnap.data();

    if (gameData?.ratingsStatus === 'STALE_OR_THIN_DATA') {
      setActionMsg(
        `Blocked: this game's ratings are flagged STALE_OR_THIN_DATA — more than half the field ` +
        `has little/no real form data. Run "♻ Refresh Form + Ratings" before opening entries.`
      );
      return;
    }

    const confirmed = window.confirm('Make this game LIVE for the public right now?');
    if (!confirmed) return;
    try {
      await updateDoc(doc(db, 'game_slates', slateId), {
        status:       'live',
        entriesOpen:  true,
        pricesLocked: true,
      });
      setActionMsg('Game is now LIVE!');
      setTimeout(() => setActionMsg(null), 3000);
    } catch (err) {
      setActionMsg(`Error: ${err.message}`);
    }
  };

  const handleCloudImport = useCallback(async () => {
    if (importStatus === IMPORT_LOADING) return;
    setImportStatus(IMPORT_LOADING);
    setImportResult(null);
    try {
      const functions = getFunctions(undefined, 'australia-southeast1');
      const importRaceFeed = httpsCallable(functions, 'importRaceFeed', { timeout: 560_000 });
      const result = await importRaceFeed({
        racingCode:  importCode,
        daysAhead:   importRange.daysAhead,
        startOffset: importRange.startOffset,
      });
      const data = result.data;
      setImportResult(data);
      setImportStatus(data.success ? IMPORT_SUCCESS : IMPORT_ERROR);
      const msg = data.success
        ? `✅ Import complete — ${data.meetingsProcessed} meetings, ${data.runnersImported} runners imported in ${data.elapsedSeconds}s`
        : `⚠️ Import finished with errors — ${data.errors?.length} error(s). Check console.`;
      setActionMsg(msg);
      if (data.errors?.length) console.error('[importRaceFeed] errors:', data.errors);
    } catch (err) {
      console.error('[importRaceFeed] call failed:', err);
      const isTimeout =
        err.code === 'deadline-exceeded' ||
        err.message?.toLowerCase().includes('timeout') ||
        err.message?.toLowerCase().includes('deadline');
      if (isTimeout) {
        setImportStatus(IMPORT_TIMEOUT);
        setImportResult(null);
        setActionMsg(
          '⏱️ Import is running on the server — check Firestore in ~30 seconds to ' +
          'confirm data arrived. The response timed out on this device but the ' +
          'import likely succeeded.'
        );
      } else {
        setImportStatus(IMPORT_ERROR);
        setImportResult(null);
        setActionMsg(`❌ Import failed: ${err.message}`);
      }
    } finally {
      setTimeout(() => { setImportStatus(IMPORT_IDLE); setActionMsg(null); }, 12_000);
    }
  }, [importStatus, importCode, importRange]);

  const handleCleanup = useCallback(async () => {
    const confirmed = window.confirm(
      '🗑️ Purge old race meeting data?\n\n' +
      'This deletes raw meeting data older than 7 days from Firestore (meetings/). ' +
      'This cannot be undone.\n\n' +
      '✅ Game slates, player entries, results and leaderboards are ' +
      'completely unaffected — players will always be able to see their ' +
      'full game history.\n\n' +
      'Continue?'
    );
    if (!confirmed) return;
    setCleanupStatus('loading');
    setCleanupResult(null);
    try {
      const functions = getFunctions(undefined, 'australia-southeast1');
      const cleanupFn = httpsCallable(functions, 'cleanupMeetings');
      const result    = await cleanupFn();
      const d = result.data;
      setCleanupResult(d);
      setCleanupStatus('done');
      const more = d.moreToDelete ? ' — run again to delete more' : '';
      setActionMsg(
        `✅ Purged ${d.deleted} old meeting records${more}. ` +
        `Cutoff: ${new Date(d.cutoffDate).toLocaleDateString('en-AU')}`
      );
    } catch (err) {
      console.error('[cleanup] failed:', err);
      setCleanupStatus('error');
      setActionMsg(`❌ Cleanup failed: ${err.message}`);
    } finally {
      setTimeout(() => { setCleanupStatus('idle'); setCleanupResult(null); setActionMsg(null); }, 12_000);
    }
  }, []);

  // ── Stable Talk — Lounge message retention ─────────────────────────────────
  async function handleLoungeDryRun() {
    setLoungeCleanupLoading('dryrun');
    setLoungeCleanupResult(null);
    try {
      const fn  = httpsCallable(getFunctions(undefined, 'australia-southeast1'), 'cleanupLoungeMessages');
      const res = await fn({ retentionDays: loungeRetentionDays, dryRun: true });
      const n   = res.data.matchedCount;
      setLoungeCleanupResult({
        type: 'info',
        msg:  `${n} Lounge message${n === 1 ? '' : 's'} older than ${loungeRetentionDays} days would be deleted.`,
      });
    } catch (e) {
      setLoungeCleanupResult({ type: 'error', msg: `Failed: ${e.message}` });
    } finally {
      setLoungeCleanupLoading(null);
    }
  }

  async function handleLoungeDelete() {
    const confirmed = window.confirm(
      `Permanently delete Lounge messages older than ${loungeRetentionDays} days? This cannot be undone.`
    );
    if (!confirmed) return;
    setLoungeCleanupLoading('delete');
    setLoungeCleanupResult(null);
    try {
      const fn  = httpsCallable(getFunctions(undefined, 'australia-southeast1'), 'cleanupLoungeMessages');
      const res = await fn({ retentionDays: loungeRetentionDays, dryRun: false });
      let msg = `Deleted ${res.data.deletedCount} Lounge message${res.data.deletedCount === 1 ? '' : 's'}.`;
      if (res.data.deletedCount === 450) msg += ' There may be more — run again.';
      setLoungeCleanupResult({ type: 'success', msg });
    } catch (e) {
      setLoungeCleanupResult({ type: 'error', msg: `Failed: ${e.message}` });
    } finally {
      setLoungeCleanupLoading(null);
    }
  }

  const handleArchiveSlate = async (slateId, gameName) => {
    const confirmed = window.confirm(
      `Archive "${gameName}"?\n\nIt will be hidden from the admin panel but players can still see their entries and results. You can show it again with "Show archived games".`
    );
    if (!confirmed) return;
    try {
      await updateDoc(doc(db, 'game_slates', slateId), {
        archived:   true,
        archivedAt: serverTimestamp(),
      });
      setActionMsg(`"${gameName}" archived.`);
      setTimeout(() => setActionMsg(null), 3000);
    } catch (err) {
      setActionMsg(`Error: ${err.message}`);
    }
  };

  const handleDeleteSlate = async (slateId, gameName) => {
    const confirmed = window.confirm(
      `⚠️ PERMANENT DELETE\n\n"${gameName}"\n\nThis cannot be undone and will break any player entries linked to this game.\n\nOnly use for test/dummy games with zero real player entries.\n\nAre you sure?`
    );
    if (!confirmed) return;
    try {
      await deleteDoc(doc(db, 'game_slates', slateId));
      setActionMsg(`"${gameName}" permanently deleted.`);
      setTimeout(() => setActionMsg(null), 3000);
    } catch (err) {
      setActionMsg(`Error: ${err.message}`);
    }
  };

  const [ratingSlateId, setRatingSlateId] = useState(null);

  const handleRateRunners = useCallback(async (slateId) => {
    if (ratingSlateId) return; // already running
    setRatingSlateId(slateId);
    try {
      const functions  = getFunctions(undefined, 'australia-southeast1');
      const rateFn     = httpsCallable(functions, 'calculateRunnerRatings');
      const result     = await rateFn({ slateId });
      const d = result.data;

      // Turn button green immediately — onSnapshot will confirm shortly after
      const ratedNow = new Date();
      setSlates(prev => prev.map(s =>
        s.id === slateId ? { ...s, ratingsCalculated: true, ratingsCalculatedAt: ratedNow } : s
      ));

      const msg = d.usedFallback
        ? `⭐ Ratings calculated (⚠️ backup model) — ${d.runnersProcessed || 0} runners rated`
        : `⭐ Ratings calculated — ${d.runnersProcessed || 0} runners rated`;
      setActionMsg(msg);
      if (d.usedFallback) {
        console.warn('[rateRunners] Fallback engine used — v2-signal primary path failed.');
      }
    } catch (err) {
      console.error('[rateRunners] failed:', err);
      setActionMsg(`❌ Rating failed: ${err.message}`);
    } finally {
      setRatingSlateId(null);
      setTimeout(() => setActionMsg(null), 8000);
    }
  }, [ratingSlateId]);

  const [refreshingSlateId, setRefreshingSlateId] = useState(null);

  const handleRefreshFormAndRatings = useCallback(async (slateId) => {
    if (refreshingSlateId) return; // already running
    setRefreshingSlateId(slateId);
    try {
      const functions = getFunctions(undefined, 'australia-southeast1');
      const refreshFn  = httpsCallable(functions, 'refreshRaceFormAndRatings');
      const result     = await refreshFn({ slateId });
      const d = result.data;

      const stillStale = (d.races || []).some(r => r.ratingsStatus !== 'OK');
      setSlates(prev => prev.map(s =>
        s.id === slateId
          ? { ...s, ratingsCalculated: !stillStale, ratingsStatus: stillStale ? 'STALE_OR_THIN_DATA' : 'OK', ratingsCalculatedAt: new Date() }
          : s
      ));

      setActionMsg(stillStale
        ? `♻ Form refreshed, but ${d.racesProcessed} race(s) are still thin/stale — check the source meeting has been re-scraped.`
        : `♻ Form + ratings refreshed for ${d.racesProcessed} race(s).`);
    } catch (err) {
      console.error('[refreshRaceFormAndRatings] failed:', err);
      setActionMsg(`❌ Refresh failed: ${err.message}`);
    } finally {
      setRefreshingSlateId(null);
      setTimeout(() => setActionMsg(null), 8000);
    }
  }, [refreshingSlateId]);

  const importBtnLabel = () => {
    if (importStatus === IMPORT_LOADING) return '⏳ Importing… (this may take a few minutes)';
    if (importStatus === IMPORT_SUCCESS) return '✅ Import Complete';
    if (importStatus === IMPORT_TIMEOUT) return '⏱️ Check Firestore';
    if (importStatus === IMPORT_ERROR)   return '❌ Import Failed — Retry?';
    return '☁️ Load Data via Cloud';
  };

  return (
    <div className="ss-adm-page">
      <AdminPlayerStatsBar />
      <div className="ss-adm-header">
        <h1 className="ss-adm-title">Admin Dashboard</h1>
        <Link to="/admin/new-game" className="ss-adm-create-btn">+ New Game</Link>
      </div>

      {actionMsg && (
        <div className={`ss-adm-action-msg ${
          importStatus === IMPORT_ERROR   ? 'ss-adm-action-msg--error'   :
          importStatus === IMPORT_TIMEOUT ? 'ss-adm-action-msg--timeout' : ''
        }`}>
          {actionMsg}
        </div>
      )}

      <TickerPanel />

      {/* Cloud Import Panel */}
      <div className="ss-adm-import-panel">
        <div className="ss-adm-import-header">
          <div>
            <h2 className="ss-adm-import-title">Race Feed Importer</h2>
            <p className="ss-adm-import-desc">
              Triggers the Birdcage pipeline in Cloud Functions — discovers today's
              meetings, scrapes runner fields, and uploads to Firestore.
              Allow up to 5 minutes for the full run.
            </p>
          </div>
        </div>
        <div className="ss-adm-import-controls">
          <div className="ss-adm-import-field">
            <label className="ss-adm-import-label">Racing Code</label>
            <select
              className="ss-admin-input ss-adm-import-select"
              value={importCode}
              onChange={e => setImportCode(e.target.value)}
              disabled={importStatus === IMPORT_LOADING}
            >
              <option value="TB">Thoroughbred</option>
              <option value="HAR">Harness</option>
              <option value="GRY">Greyhound</option>
              <option value="all">All codes</option>
            </select>
          </div>
          <div className="ss-adm-import-field">
            <label className="ss-adm-import-label">Days Ahead</label>
            <select
              className="ss-admin-input ss-adm-import-select"
              value={importRange.label}
              onChange={e => {
                const ranges = {
                  'today':    { daysAhead: 1, startOffset: 0 },
                  'tomorrow': { daysAhead: 1, startOffset: 1 },
                  'plus234':  { daysAhead: 3, startOffset: 2 },
                };
                setImportRange({ ...ranges[e.target.value], label: e.target.value });
              }}
              disabled={importStatus === IMPORT_LOADING}
            >
              <option value="today">Today only</option>
              <option value="tomorrow">Tomorrow only</option>
              <option value="plus234">+Days 2, 3, 4</option>
            </select>
          </div>
          <button
            className={`ss-adm-import-btn ${
              importStatus === IMPORT_LOADING ? 'ss-adm-import-btn--loading' :
              importStatus === IMPORT_SUCCESS ? 'ss-adm-import-btn--success' :
              importStatus === IMPORT_TIMEOUT ? 'ss-adm-import-btn--timeout' :
              importStatus === IMPORT_ERROR   ? 'ss-adm-import-btn--error'   : ''
            }`}
            onClick={handleCloudImport}
            disabled={importStatus === IMPORT_LOADING}
          >
            {importStatus === IMPORT_LOADING && <span className="ss-adm-import-spinner" />}
            {importBtnLabel()}
          </button>
          <button
            type="button"
            className={`ss-adm-import-btn ss-adm-purge-btn ${
              cleanupStatus === 'loading' ? 'ss-adm-import-btn--loading' :
              cleanupStatus === 'done'    ? 'ss-adm-import-btn--success' :
              cleanupStatus === 'error'   ? 'ss-adm-import-btn--error'   : ''
            }`}
            onClick={handleCleanup}
            disabled={cleanupStatus === 'loading' || importStatus === IMPORT_LOADING}
            title="Delete raw meeting data older than 7 days. Game slates and player history are not affected."
          >
            {cleanupStatus === 'loading' ? '⏳ Purging…' :
             cleanupStatus === 'done'    ? '✅ Purged'   :
             cleanupStatus === 'error'   ? '❌ Failed'   : '🗑️ Purge Old Data'}
          </button>
        </div>
        {importResult && importStatus !== IMPORT_LOADING && (
          <div className="ss-adm-import-result">
            <span>Meetings: <strong>{importResult.meetingsProcessed}</strong></span>
            <span>Runners: <strong>{importResult.runnersImported}</strong></span>
            <span>Time: <strong>{importResult.elapsedSeconds}s</strong></span>
            {importResult.targetDates?.length > 0 && (
              <span>Dates: <strong>{importResult.targetDates.join(', ')}</strong></span>
            )}
            {importResult.errors?.length > 0 && (
              <span className="ss-adm-import-errors">
                {importResult.errors.length} error(s) — see browser console
              </span>
            )}
          </div>
        )}
        {cleanupResult && cleanupStatus !== 'loading' && (
          <div className="ss-adm-import-result">
            <span>Deleted: <strong>{cleanupResult.deleted ?? 0}</strong> meeting records</span>
            <span>Kept: <strong>{cleanupResult.kept ?? 0}</strong> · Skipped: <strong>{cleanupResult.skipped ?? 0}</strong></span>
            {cleanupResult.cutoffDate && (
              <span>Older than: <strong>{new Date(cleanupResult.cutoffDate).toLocaleDateString('en-AU')}</strong></span>
            )}
          </div>
        )}
      </div>

      {/* ── Stable Talk Cleanup ───────────────────────────────────────────────── */}
      <div className="ss-adm-import-panel">
        <div className="ss-adm-import-header">
          <div>
            <h2 className="ss-adm-import-title">🐎 Stable Talk — Lounge Cleanup</h2>
            <p className="ss-adm-import-desc">
              Manage retention of public Lounge messages. Private messages, team chats,
              and all player game history are never affected.
            </p>
          </div>
        </div>

        <div className="admin-cleanup__row">
          <label className="admin-cleanup__label">LOUNGE RETENTION</label>
          <select
            className="ss-admin-input admin-cleanup__select"
            value={loungeRetentionDays}
            onChange={e => { setLoungeRetentionDays(Number(e.target.value)); setLoungeCleanupResult(null); }}
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days (default)</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
          </select>
        </div>

        <div className="admin-cleanup__actions">
          <button
            className="admin-btn admin-btn--secondary"
            onClick={handleLoungeDryRun}
            disabled={!!loungeCleanupLoading}
          >
            {loungeCleanupLoading === 'dryrun' ? 'Counting…' : '🔍 Dry Run / Count Messages'}
          </button>
          <button
            className="admin-btn admin-btn--danger"
            onClick={handleLoungeDelete}
            disabled={!!loungeCleanupLoading}
          >
            {loungeCleanupLoading === 'delete' ? 'Deleting…' : '🗑️ Delete Old Lounge Messages'}
          </button>
        </div>

        {loungeCleanupResult && (
          <div className={`admin-status admin-status--${loungeCleanupResult.type}`}>
            {loungeCleanupResult.msg}
          </div>
        )}

        <p className="admin-cleanup__warning">
          ⚠️ Deletes public Lounge messages older than the selected period.
          Private and team messages are not affected.
        </p>
      </div>

      <div className="ss-adm-table">
        <div className="ss-adm-table-toolbar">
          <label className="ss-adm-show-archived">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={e => setShowArchived(e.target.checked)}
            />
            Show archived games
          </label>
          {showArchived && (
            <span className="ss-adm-archived-count">
              {slates.filter(s => s.archived).length} archived
            </span>
          )}
        </div>

        {loading ? (
          <div>Loading games…</div>
        ) : slates
            .filter(s => showArchived || s.archived !== true)
            .map(slate => (
              <SlateRow
                key={slate.id}
                slate={slate}
                onOpenEntries={handleOpenEntries}
                onForceOpen={handleForceOpen}
                onDelete={handleDeleteSlate}
                onArchive={handleArchiveSlate}
                onRateRunners={handleRateRunners}
                isRating={ratingSlateId === slate.id}
                onRefreshFormAndRatings={handleRefreshFormAndRatings}
                isRefreshing={refreshingSlateId === slate.id}
              />
            ))}
      </div>
    </div>
  );
};

export default AdminSlatesPage;
